// AWS Organization connections (#914, slice 1 of #792): the minimal status view. For each
// connection, its discovered accounts with their membership, their role's verification,
// their regions and the time of the last complete check. Read off `api/awsOrganizations.ts`.
//
// What a person reads is said in words on the page, never only in a `title`: a phone and
// a keyboard reach neither a tooltip nor a hover (#877).
//
// Nothing here connects, admits or shares an account. Starting a connection is the setup
// wizard's (#921), and sharing with an organisation is #916's.

import { useEffect, useState } from "react";

import {
  discoverAgain,
  readOrganizations,
  type Candidate,
  type OrganizationConnection,
  type OrganizationsRead,
  type RegionStatus,
} from "../api/awsOrganizations";
import { browserApi, messageOf, type Api } from "../api/http";
import { listRoles, roleHolds, type Role } from "../api/roles";
import { onOwnMembership, type SessionResponse } from "../api/session";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { readableTime } from "./runPresentation";

const MANAGE_PERMISSION = "accounts:manage";

/** AWS's account states, as a person reads them. */
const ACCOUNT_STATES: Record<string, string> = {
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  PENDING_ACTIVATION: "Not yet active",
  PENDING_CLOSURE: "Closing",
  CLOSED: "Closed",
};

/** The regional StackSet's instance statuses, as a person reads them. */
const REGION_STATUSES: Record<string, string> = {
  SUCCEEDED: "Deployed",
  FAILED: "Failed",
  RUNNING: "Deploying",
  PENDING: "Deploying",
  CANCELLED: "Cancelled",
  INOPERABLE: "Inoperable",
  SKIPPED_SUSPENDED_ACCOUNT: "Skipped: account suspended",
  NOT_DEPLOYED: "Not deployed",
};

export function accountStateText(state: string): string {
  return ACCOUNT_STATES[state] ?? state.toLowerCase().replace(/_/g, " ");
}

/** Where the account stands in the Organization, from the last complete check. */
export function membershipText(candidate: Candidate): string {
  const m = candidate.membership;
  if (m === null) return "Not read yet";
  const now = !m.listed
    ? "Left the Organization"
    : `${m.in_scope ? "In scope" : "Outside the chosen scope"} · ${accountStateText(m.account_state)}`;
  return m.unknown ? `Unknown: the last check did not finish. Last known: ${now}` : now;
}

/** Whether the platform has assumed the role the account reported. */
export function verificationText(candidate: Candidate): string {
  const v = candidate.verification;
  if (v.state === "verified") return `Role verified ${readableTime(v.verified_at)}`;
  if (v.state === "failed") return `Role check failed: ${v.error ?? "no reason was given"}`;
  return v.reported_role_arn === null ? "Role not reported yet" : "Role reported, not verified yet";
}

export function regionText(region: RegionStatus): string {
  const status = REGION_STATUSES[region.status] ?? region.status.toLowerCase().replace(/_/g, " ");
  return region.unknown ? `${status} (unknown: the last check did not finish)` : status;
}

function CandidateItem({ candidate }: { candidate: Candidate }) {
  const headingId = `candidate-${candidate.account_id}`;
  return (
    <li className="panel__body" aria-labelledby={headingId}>
      <h3 id={headingId}>{candidate.name || candidate.account_id}</h3>
      <dl className="kv">
        <dt>Account</dt>
        <dd>{candidate.account_id}</dd>
        <dt>Membership</dt>
        <dd>{membershipText(candidate)}</dd>
        <dt>Role</dt>
        <dd>{verificationText(candidate)}</dd>
        <dt>Regions</dt>
        <dd>
          {candidate.regions.length === 0 ? (
            "None read yet"
          ) : (
            <ul className="list" aria-label={`Regions of ${candidate.account_id}`}>
              {candidate.regions.map((region) => (
                <li key={region.region}>
                  {region.region}: {regionText(region)}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </dl>
      {candidate.manual_cloud_account_id === null ? null : (
        <p className="muted">
          <Chip>Connected manually</Chip> This account is already connected on its own; this connection leaves it
          as it is.
        </p>
      )}
    </li>
  );
}

export function ConnectionPanel({
  connection,
  onDiscover,
}: {
  connection: OrganizationConnection;
  /** Absent for someone who may not run discovery: the server refuses them. */
  onDiscover?: (connectionId: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingId = `organization-${connection.id}`;
  const check = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDiscover!(connection.id);
    } catch (failed) {
      setError(messageOf(failed));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel" aria-labelledby={headingId}>
      <div className="panel__head">
        <h2 id={headingId}>AWS Organization {connection.organization_id}</h2>
        {onDiscover === undefined ? null : (
          <button type="button" className="btn btn--small" onClick={() => void check()} disabled={busy}>
            {busy ? "Checking…" : "Check again"}
          </button>
        )}
      </div>
      <div className="panel__body stack stack--tight">
        <dl className="kv">
          <dt>Management account</dt>
          <dd>{connection.management_account_id}</dd>
          <dt>Scope</dt>
          <dd>
            {connection.scope.entire_organization
              ? "The entire Organization"
              : `These OUs: ${connection.scope.ou_ids.join(", ")}`}
          </dd>
          <dt>Last complete check</dt>
          <dd>{connection.last_complete_at === null ? "Never" : readableTime(connection.last_complete_at)}</dd>
        </dl>
        {connection.last_error === null ? null : (
          <Notice tone="bad">
            The last check did not finish: {connection.last_error}. The accounts below are as the last complete
            check found them.
          </Notice>
        )}
        {error === null ? null : <Notice tone="bad">Could not check again: {error}</Notice>}
      </div>
      {connection.candidates.length === 0 ? (
        <p className="panel__body muted">No accounts discovered yet.</p>
      ) : (
        <ul className="list" aria-label={`Accounts in ${connection.organization_id}`}>
          {connection.candidates.map((candidate) => (
            <CandidateItem key={candidate.account_id} candidate={candidate} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function AwsOrganizationPage({
  read,
  error,
  onDiscover,
}: {
  read: OrganizationsRead | null;
  error: string | null;
  onDiscover?: (connectionId: string) => Promise<void>;
}) {
  return (
    <>
      <PageIntro destination="awsOrganization" />
      {error !== null ? (
        <Notice tone="bad">{error}</Notice>
      ) : read === null ? (
        <p className="muted" role="status">
          Reading this workspace's AWS Organization connections…
        </p>
      ) : read.connections.length === 0 ? (
        <Empty title="No AWS Organization is connected">
          <p className="muted">This workspace has no AWS Organization connection.</p>
        </Empty>
      ) : (
        <div className="stack">
          {read.connections.map((connection) => (
            <ConnectionPanel key={connection.id} connection={connection} onDiscover={onDiscover} />
          ))}
        </div>
      )}
    </>
  );
}

/** Whether this session may run discovery, as the server would answer it. */
export function mayDiscover(session: SessionResponse, roles: Role[] | null): boolean {
  return onOwnMembership(session) && roleHolds(roles, session.role, MANAGE_PERMISSION);
}

export function AwsOrganizationScreen({ session, api = browserApi }: { session: SessionResponse; api?: Api }) {
  const [read, setRead] = useState<OrganizationsRead | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What this session's role allows, read here: the console reads the roles only on the
  // screens that need them, and this is one. Until it is read, nothing is offered.
  const [roles, setRoles] = useState<Role[] | null>(null);
  useEffect(() => {
    readOrganizations(api).then(setRead, (failed) => setError(messageOf(failed)));
  }, [api]);
  useEffect(() => {
    listRoles(api).then(setRoles, () => setRoles(null));
  }, [api]);
  const discover = async (connectionId: string) => {
    const after = await discoverAgain(api, connectionId);
    setRead((was) =>
      was === null ? was : { connections: was.connections.map((c) => (c.id === after.id ? after : c)) },
    );
  };
  return (
    <AwsOrganizationPage read={read} error={error} onDiscover={mayDiscover(session, roles) ? discover : undefined} />
  );
}
